import React, { ChangeEventHandler, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { noop } from "lodash";
import { Button, Chip, MultiselectOption } from "@shared/components";
import InvestorRequestEntitiesDropdowns from "@investorRequest/components/InvestorRequestEntitiesDropdowns";
import InvestorRequestTextarea from "@investorRequest/components/InvestorRequestTextarea";
import InvestorRequestDatepicker from "@investorRequest/components/InvestorRequestDatepicker";
import SubmitAnotherFormDialog from "@investorRequest/components/SubmitAnotherFormDialog";
import FieldTooltip from "@investorRequest/components/FieldTooltip";
import BankExtractionProcessingCard from "@investorRequest/components/BankExtractionProcessingCard";
import BankExtractionTotalFailureCard from "@investorRequest/components/BankExtractionTotalFailureCard";
import { entitledFundsSelector, entitledAccountsSelector } from "@store/Entities/selectors";
import { fetchEntitledAccountsByEntity } from "@store/Entities/thunks";
import { investorRequestSelector } from "@investorRequest/store/selectors";
import {
  updateCommonRequestData,
  updateCustomRequestData,
  clearSubmitSuccess,
  closeInvestorRequestDialog,
  setBankExtractionFileError,
  resetBankExtractionForRetry,
  setWireFormStep,
  setBankExtractionPhase,
} from "@investorRequest/store/ducks";
import { cancelBankExtraction } from "@investorRequest/store/thunks";
import {
  clearPendingBankExtractionFile,
  setPendingBankExtractionFile,
} from "@investorRequest/utils/bankExtractionFileHolder";
import { toEffectiveDateString } from "@investorRequest/utils/effectiveDate";
import { useAppDispatch, useAppSelector, useMobileLayout } from "@app/hooks";
import FileUtils from "@shared/utils/FileUtils";
import { MimeType } from "@shared/models/mimeType";
import CommonUtils from "@shared/utils/CommonUtils";
import { Nullable } from "@shared/models/general";
import {
  BANK_EXTRACTION_ALLOWED_TYPES,
  BANK_EXTRACTION_MAX_FILE_BYTES,
  bankExtractionCopy,
} from "@investorRequest/constants/bankExtractionCopy";
import { ReactComponent as Clip } from "@assets/svg/clip.svg";
import { ReactComponent as ImageIcon } from "@assets/svg/file_image.svg";
import { ReactComponent as PdfIcon } from "@assets/svg/file_pdf.svg";
import { ReactComponent as WordIcon } from "@assets/svg/file_word.svg";
import { ReactComponent as DocumentIcon } from "@assets/svg/file_document.svg";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg";

const useStyles = makeStyles((theme) => ({
  dateRow: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 76,
    padding: theme.spacing(0, 6),
    [theme.breakpoints.down("md")]: {
      alignItems: "stretch",
      flexDirection: "column",
      padding: theme.spacing(4, 5),
      rowGap: theme.spacing(2),
    },
  },
  dateLabel: {
    fontSize: 20,
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: "0.02em",
  },
  dateControl: {
    width: 304,
    "& [data-testid='datepicker-toggle-button']": {
      height: 44,
      padding: 0,
      borderBottom: `1px solid ${theme.palette.primary.main}`,
      justifyContent: "space-between",
    },
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
  },
  dropzone: {
    alignItems: "center",
    border: `1px solid ${theme.palette.grey[900]}`,
    color: theme.palette.primary.main,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(6),
    justifyContent: "center",
    minHeight: 200,
    padding: theme.spacing(7, 8),
    textAlign: "center",
  },
  dropzoneDisabled: {
    opacity: 0.6,
    pointerEvents: "none",
  },
  dropzoneHeadline: {
    color: theme.palette.primary.main,
    fontSize: 20,
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: "0.02em",
  },
  dropzoneHelper: {
    color: theme.palette.primary.main,
    fontSize: 14,
    fontStyle: "italic",
    letterSpacing: "0.02em",
  },
  notesWrapper: {
    paddingTop: theme.spacing(5),
  },
}));

const validateBankExtractionFile = (file: File): string | null => {
  if (!BANK_EXTRACTION_ALLOWED_TYPES.has(file.type)) {
    return bankExtractionCopy.fileTypeError;
  }

  if (file.size > BANK_EXTRACTION_MAX_FILE_BYTES) {
    return bankExtractionCopy.fileSizeError;
  }

  return null;
};

const WireInstructionsUploadRequest = () => {
  const classes = useStyles();
  const { validationWarningShown, commonFields, customFields, submitSuccess, bankExtraction } =
    useAppSelector(investorRequestSelector);
  const dispatch = useAppDispatch();
  const isMobile = useMobileLayout();
  const [showSubmitAnotherDialog, setShowSubmitAnotherDialog] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;

    const media = window.matchMedia("(prefers-reduced-motion: reduce)");

    setReducedMotion(Boolean(media?.matches));
    const handler = () => setReducedMotion(media.matches);

    media.addEventListener("change", handler);

    return () => media.removeEventListener("change", handler);
  }, []);

  const entitledFundsMap = useAppSelector(entitledFundsSelector).data;
  const entitledAccountsMap = useAppSelector(entitledAccountsSelector).data;

  const isAdmin = useMemo(() => {
    if (!entitledFundsMap) return false;

    return Object.values(entitledFundsMap).some((fund) => fund.isAdmin === true);
  }, [entitledFundsMap]);

  const selectedAccounts = useAppSelector((state) => state.investorRequest.commonFields.selectedAccounts);
  const selectedFunds = useAppSelector((state) => state.investorRequest.commonFields.selectedFunds);
  const selectedAccountsMap = CommonUtils.convertArrayToMap(selectedAccounts);
  const selectedFundsMap = CommonUtils.convertArrayToMap(selectedFunds);

  const funds = useMemo<MultiselectOption[]>(() => {
    if (!entitledFundsMap) return [];

    return CommonUtils.convertMapToArray(entitledFundsMap, (fund) => ({
      id: fund.globalId,
      label: fund.entityName,
      selected: !!selectedFundsMap[fund.globalId],
    })).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [entitledFundsMap, selectedFundsMap]);

  const accounts = useMemo<MultiselectOption[]>(() => {
    if (!entitledAccountsMap) return [];

    return CommonUtils.convertMapToArray(entitledAccountsMap, (account) => ({
      id: account.globalId,
      label: account.name,
      selected: !!selectedAccountsMap[account.globalId],
    })).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [entitledAccountsMap, selectedAccountsMap]);

  const filteredFundsOptions = funds;
  const fetchedFundsRef = useRef<Set<string>>(new Set());

  const filteredAccountsOptions = useMemo(() => {
    if (!entitledAccountsMap) return accounts;

    if (isAdmin) {
      return CommonUtils.convertMapToArray(entitledAccountsMap, (account) => ({
        id: account.globalId,
        label: account.name,
        selected: !!selectedAccountsMap[account.globalId],
      })).sort((a, b) => (a.label || "").localeCompare(b.label || ""));
    }

    if (!commonFields.selectedFunds || commonFields.selectedFunds.length === 0) return [];

    const allowedAccountIds = new Set<string>();

    commonFields.selectedFunds.forEach((fundId) => {
      entitledFundsMap?.[fundId]?.entitledAccountIds?.forEach((accountId) => allowedAccountIds.add(accountId));
    });

    if (allowedAccountIds.size === 0) {
      return accounts.map((account) => ({
        ...account,
        selected: !!selectedAccountsMap[account.id as string],
      }));
    }

    return CommonUtils.convertMapToArray(entitledAccountsMap, (account) => account)
      .filter((account) => allowedAccountIds.has(account.globalId))
      .map((account) => ({
        id: account.globalId,
        label: account.name,
        selected: !!selectedAccountsMap[account.globalId],
      }))
      .sort((a, b) => (a.label || "").localeCompare(b.label || ""));
  }, [entitledAccountsMap, accounts, isAdmin, selectedAccountsMap, commonFields.selectedFunds, entitledFundsMap]);

  useEffect(() => {
    if (isAdmin) return;

    if (entitledFundsMap) {
      filteredFundsOptions.forEach((fundOption) => {
        const fund = entitledFundsMap[fundOption.id];
        const fundId = fundOption.id as string;

        if (fund && !fund.entitledAccountIds && !fetchedFundsRef.current.has(fundId)) {
          fetchedFundsRef.current.add(fundId);
          dispatch(fetchEntitledAccountsByEntity(fundId));
        }
      });
    }

    const currentSelected = commonFields.selectedAccounts;

    if (!currentSelected || currentSelected.length === 0) return;

    const validIds = new Set(filteredAccountsOptions.map((a) => a.id));
    const stillValid = currentSelected.filter((id) => validIds.has(id));

    if (stillValid.length !== currentSelected.length) {
      dispatch(updateCommonRequestData({ selectedAccounts: stillValid }));
    }
  }, [
    dispatch,
    filteredFundsOptions,
    entitledFundsMap,
    isAdmin,
    commonFields.selectedAccounts,
    filteredAccountsOptions,
  ]);

  useEffect(() => {
    if (!submitSuccess) return;

    setShowSubmitAnotherDialog(true);
  }, [submitSuccess]);

  const handleSubmitAnotherYes = useCallback(() => {
    setShowSubmitAnotherDialog(false);
    dispatch(clearSubmitSuccess());
    dispatch(updateCommonRequestData({ selectedFunds: [], selectedAccounts: [], message: "", attachment: null }));
    dispatch(updateCustomRequestData({ effectiveDate: "" }));
    dispatch(resetBankExtractionForRetry());
  }, [dispatch]);

  const handleSubmitAnotherNo = useCallback(() => {
    setShowSubmitAnotherDialog(false);
    dispatch(clearSubmitSuccess());
    dispatch(closeInvestorRequestDialog());
  }, [dispatch]);

  const AttachmentIcon = useMemo(() => {
    switch (commonFields.attachment?.type) {
      case MimeType.JPEG:
      case MimeType.PNG:
        return ImageIcon;
      case MimeType.PDF:
        return PdfIcon;
      case MimeType.DOCX:
        return WordIcon;
      default:
        return DocumentIcon;
    }
  }, [commonFields.attachment]);

  const attachFile = useCallback(
    async (file: File) => {
      if (bankExtraction.phase === "processing") {
        cancelBankExtraction();
        dispatch(setBankExtractionPhase("idle"));
      }

      const validationError = validateBankExtractionFile(file);

      if (validationError) {
        dispatch(setBankExtractionFileError(validationError));
        clearPendingBankExtractionFile();
        dispatch(updateCommonRequestData({ attachment: null }));

        return;
      }

      dispatch(setBankExtractionFileError(null));
      setPendingBankExtractionFile(file);

      const base64 = await FileUtils.toBase64String(file);

      dispatch(
        updateCommonRequestData({
          attachment: {
            base64,
            name: file.name,
            type: file.type as MimeType,
          },
        })
      );
    },
    [bankExtraction.phase, dispatch]
  );

  const handleAttachFile = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (event) => {
      const file = event.target.files?.[0];

      if (!file) return;

      await attachFile(file);
    },
    [attachFile]
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      const file = event.dataTransfer.files?.[0];

      if (!file) return;

      await attachFile(file);
    },
    [attachFile]
  );

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  const handleRemoveAttachment = useCallback(() => {
    clearPendingBankExtractionFile();
    dispatch(setBankExtractionFileError(null));
    dispatch(updateCommonRequestData({ attachment: null }));
  }, [dispatch]);

  const handleTryAnotherDocument = useCallback(() => {
    dispatch(resetBankExtractionForRetry());
  }, [dispatch]);

  const handleFillManually = useCallback(() => {
    dispatch(setWireFormStep("fillManually"));
    dispatch(setBankExtractionPhase("idle"));
  }, [dispatch]);

  const handleMessageChange = useCallback(
    (message: string) => dispatch(updateCommonRequestData({ message })),
    [dispatch]
  );

  const handleDatepickerChange = useCallback(
    (value: Nullable<Date>) =>
      dispatch(updateCustomRequestData({ effectiveDate: value ? toEffectiveDateString(value) : "" })),
    [dispatch]
  );

  const showDropZone = bankExtraction.phase === "idle" || bankExtraction.phase === "review";
  const showProcessing = bankExtraction.phase === "processing";
  const showFailure = bankExtraction.phase === "totalFailure";
  const dropZoneDisabled = !!commonFields.attachment || showProcessing;

  if (showProcessing) {
    return (
      <>
        <div data-testid="wire-instructions-upload-request" />
        <Grid item xs={12}>
          <BankExtractionProcessingCard activeStep={bankExtraction.processingStep} reducedMotion={reducedMotion} />
        </Grid>
      </>
    );
  }

  return (
    <>
      <div data-testid="wire-instructions-upload-request" />
      <InvestorRequestEntitiesDropdowns
        required
        accounts={filteredAccountsOptions}
        funds={filteredFundsOptions}
        accountsDisabled={!isAdmin && commonFields.selectedFunds.length === 0}
      />
      <Grid item container xs={12} columnSpacing={isMobile ? undefined : 7} rowSpacing={isMobile ? 5 : 5}>
        <Grid item xs={12} data-testid="investor-request-datepicker">
          <FieldTooltip title="Date Instructions Become Effective" placement="top">
            <Box className={classes.dateRow}>
              <Typography className={classes.dateLabel}>Date Instructions Become Effective</Typography>
              <Box className={classes.dateControl}>
                <InvestorRequestDatepicker
                  value={customFields.effectiveDate || ""}
                  onChange={handleDatepickerChange}
                  format="M/d/yyyy"
                  label=""
                />
              </Box>
            </Box>
          </FieldTooltip>
        </Grid>
        <Grid item xs={12}>
          {showFailure ? (
            <BankExtractionTotalFailureCard
              detectedPaymentMethod={bankExtraction.extractionResult?.detectedPaymentMethod}
              onTryAnother={handleTryAnotherDocument}
              onFillManually={handleFillManually}
            />
          ) : showDropZone ? (
            <>
              <Box
                data-testid="wire-upload-dropzone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className={`${classes.dropzone} ${dropZoneDisabled ? classes.dropzoneDisabled : ""}`}
                sx={{
                  borderColor:
                    (validationWarningShown && !commonFields.attachment) || bankExtraction.fileError
                      ? "warning.main"
                      : "grey.900",
                }}
              >
                <Typography className={classes.dropzoneHeadline}>
                  Drag and drop your document here, or click to browse.
                </Typography>
                <Button
                  upload
                  size={isMobile ? "small" : "medium"}
                  onClick={noop}
                  shape="rounded"
                  variant="outlined"
                  startIcon={<Clip />}
                  disabled={dropZoneDisabled}
                  padding="0 36px"
                >
                  Choose a Document
                  <input
                    type="file"
                    id="attachment-upload"
                    name="attachment-upload"
                    hidden
                    accept={ACCEPT}
                    value=""
                    onChange={handleAttachFile}
                    disabled={dropZoneDisabled}
                  />
                </Button>
                <Typography className={classes.dropzoneHelper}>Supported: PDF, JPEG, PNG, DOCX</Typography>
              </Box>
              {bankExtraction.fileError ? (
                <Box mt={1} color="warning.main" fontSize={12} data-testid="bank-extraction-file-error">
                  {bankExtraction.fileError}
                </Box>
              ) : null}
              {validationWarningShown && !commonFields.attachment ? (
                <Box mt={1} color="warning.main" fontSize={12}>
                  {bankExtractionCopy.attachValidation}
                </Box>
              ) : null}
              {commonFields.attachment ? (
                <Box mt={3}>
                  <Chip Icon={AttachmentIcon} label={commonFields.attachment.name} onDelete={handleRemoveAttachment} />
                </Box>
              ) : null}
            </>
          ) : null}
        </Grid>
        <Grid item xs={12}>
          <Box className={classes.notesWrapper}>
            <InvestorRequestTextarea
              placeholder="Please include additional notes (optional)"
              value={commonFields.message}
              onChange={handleMessageChange}
            />
          </Box>
        </Grid>
      </Grid>
      <SubmitAnotherFormDialog
        open={showSubmitAnotherDialog}
        onYes={handleSubmitAnotherYes}
        onNo={handleSubmitAnotherNo}
      />
    </>
  );
};

export default memo(WireInstructionsUploadRequest);
